import {
  formatCpf,
  salespersonInputSchema,
  salespersonUpdateSchema,
  toFormNumber,
  type Salesperson,
} from "@cafegestor/shared";
import { useState, type FormEvent } from "react";
import { Button, Card, Field, Input, PageTitle, Select, Spinner, Table } from "@/components/ui";
import { useCan } from "@/app/me";
import {
  issuesToErrors,
  str,
  useCadastroList,
  useCadastroOptions,
  useForm,
  useListState,
  useSave,
} from "./lib";
import { ListToolbar, Pager, SaveError, StatusBadge } from "./widgets";
import { useAutoCode } from "./auto-code";
import { CodeField } from "./CodeField";

export function SalespeoplePage() {
  const canEdit = useCan()("cadastros.vendedores.gerenciar");
  const ls = useListState();
  const [editing, setEditing] = useState<Salesperson | "new" | null>(null);
  const list = useCadastroList<Salesperson>("vendedores", ls);

  return (
    <div className="space-y-6">
      <PageTitle
        title="Vendedores"
        description="Quem vende e leva carga. Ligar a um usuário faz com que ele veja só os próprios pedidos, cargas e notas."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Novo vendedor
            </Button>
          )
        }
      />
      {editing && (
        <SalespersonEditor
          key={editing === "new" ? "new" : editing.id}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        <ListToolbar q={ls.q} onQ={ls.setQ} status={ls.status} onStatus={ls.setStatus} />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Código", "Nome", "CPF", "Usuário", "Comissão", "Situação", ""]}>
              {list.data?.items.map((s) => (
                <tr key={s.id}>
                  <td className="px-3 py-2 font-mono">{s.code}</td>
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2">{s.document ? formatCpf(s.document) : "—"}</td>
                  <td className="px-3 py-2">{s.userEmail ?? "—"}</td>
                  <td className="px-3 py-2">
                    {Number(s.commissionPercent).toLocaleString("pt-BR")}%
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>
                        Editar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={ls.offset} onOffset={ls.setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function SalespersonEditor({ item, onClose }: { item: Salesperson | null; onClose: () => void }) {
  const options = useCadastroOptions();
  const f = useForm({
    code: str(item?.code),
    name: str(item?.name),
    document: str(item?.document),
    email: str(item?.email),
    phone: str(item?.phone),
    userId: str(item?.userId),
    commissionPercent: item ? toFormNumber(item.commissionPercent) : "0",
    notes: str(item?.notes),
    status: item?.status ?? "active",
  });
  const auto = useAutoCode("vendedor");
  const save = useSave<unknown, Salesperson>({
    resource: "vendedores",
    id: item?.id ?? null,
    invalidate: ["vendedores", "opcoes"],
    onSaved: onClose,
    successMessage: (r, created) =>
      created
        ? `Vendedor criado com sucesso. Código: ${r.code}${auto.note(r.code)}.`
        : "Vendedor atualizado com sucesso.",
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = item
      ? salespersonUpdateSchema.safeParse({ ...f.values, version: item.version })
      : salespersonInputSchema.safeParse({ ...f.values, code: auto.forSave(f.values.code) });
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title={item ? `Editar ${item.name}` : "Novo vendedor"}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3">
        <CodeField
          auto={auto}
          value={f.values.code}
          onChange={(v) => f.set("code")(v)}
          error={f.errors["code"]}
        />
        <Field label="Nome" error={f.errors["name"]} required>
          <Input {...f.text("name")} />
        </Field>
        <Field label="CPF" error={f.errors["document"]}>
          <Input inputMode="numeric" {...f.text("document")} />
        </Field>
        <Field label="E-mail" error={f.errors["email"]}>
          <Input type="email" {...f.text("email")} />
        </Field>
        <Field label="Telefone" error={f.errors["phone"]}>
          <Input {...f.text("phone")} />
        </Field>
        <Field label="Comissão (%)" error={f.errors["commissionPercent"]}>
          <Input inputMode="decimal" {...f.text("commissionPercent")} />
        </Field>
        <Field
          label="Usuário do sistema"
          error={f.errors["userId"]}
          hint="Opcional. Só usuários com acesso a esta empresa."
        >
          <Select {...f.text("userId")}>
            <option value="">Nenhum</option>
            {options.data?.members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.fullName ? `${m.fullName} (${m.email})` : m.email}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Situação">
          <Select {...f.text("status")}>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
          </Select>
        </Field>
        <Field label="Observações" error={f.errors["notes"]}>
          <Input {...f.text("notes")} />
        </Field>
        <div className="flex gap-2 sm:col-span-3">
          <Button type="submit" disabled={save.isPending}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <div className="sm:col-span-3">
          <SaveError error={save.error} />
        </div>
      </form>
    </Card>
  );
}
